import React from "react";
import { Button, DatePicker, Input, Select } from "antd";
import dayjs from "dayjs";

import MemberDobSuggestion from "./MemberDobSuggestion";
import {
  FIELD_LABELS,
  RELATION_OPTIONS,
  REVIEW_FIELDS,
} from "../../lib/policyCompleteness";

// --- PMS suggestion strip ----------------------------------------------------
// The approve step shows what the PMS (Sikka) says for each field next to what's
// in the form, so the user can pick. For a field the office previously LOCKED to
// a different value it also names that prior value — "you set this to X, PMS says
// Y" — because whatever they leave in the form on Approve is written, lock or not.

function relationLabel(code) {
  const opt = (RELATION_OPTIONS || []).find(function (o) { return o.value === code; });
  return opt ? opt.label : code;
}

// Display form for a value: date without the time, relation as its human label.
function displayValue(field, v) {
  if (v == null || String(v).trim() === "") return "";
  if (field === "date_of_birth") return String(v).split("T")[0];
  if (field === "relation_to_member") return relationLabel(String(v).trim());
  return String(v);
}

// Comparison form: what counts as "the same value" (case/time-insensitive) so an
// identical PMS value doesn't render a pointless suggestion.
function normValue(field, v) {
  if (v == null) return "";
  if (field === "date_of_birth") return String(v).split("T")[0];
  return String(v).trim().toLowerCase();
}

// The value to write into the form when "Use" is clicked.
function useValue(field, v) {
  if (field === "date_of_birth") return String(v).split("T")[0];
  return v;
}

function PmsSuggestion({ field, currentValue, preview, onUse }) {
  if (!preview) return null;
  // The same strip for a claim form the office uploaded: its preview carries
  // `values` and says where it came from.
  const source = preview.source === "claim_form" ? "Claim form" : "PMS";
  const sikka = (preview.values || preview.sikka_values || {})[field];
  if (sikka == null || String(sikka).trim() === "") return null;
  if (normValue(field, sikka) === normValue(field, currentValue)) return null;

  const locked = (preview.field_states || {})[field] === "manually_edited";
  const snap = (preview.local_snapshot || {})[field];
  const prior = snap != null && String(snap).trim() !== "" ? snap : currentValue;
  const priorDisplay = displayValue(field, prior);
  const sikkaDisplay = displayValue(field, sikka);

  return (
    <div
      style={{
        marginTop: 4,
        padding: "6px 10px",
        borderRadius: 8,
        background: "rgba(107,67,181,0.06)",
        border: "1px solid rgba(107,67,181,0.18)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        fontSize: 12.5,
      }}
    >
      <span role="img" aria-label="suggestion">💡</span>
      {locked ? (
        <span style={{ color: "#5b4a7e" }}>
          You previously set this to <strong>{priorDisplay || "(blank)"}</strong> — {source}{" "}
          says <strong>{sikkaDisplay}</strong>.
        </span>
      ) : (
        <span style={{ color: "#5b4a7e" }}>
          {source}: <strong>{sikkaDisplay}</strong>
        </span>
      )}
      <Button
        type="link"
        size="small"
        style={{ padding: 0, height: "auto" }}
        onClick={function () { onUse(useValue(field, sikka)); }}
      >
        Use
      </Button>
    </div>
  );
}

// A claim-form plan's member name / ID, which the form never prints, filled from
// our own records (claim-form/policies/ `on_file`). Said under the field while
// it still holds that value, so nobody approves it believing the form said so.
function OnFileNote({ field, currentValue, preview }) {
  const onFile = preview && preview.on_file;
  const value = onFile && onFile.values ? onFile.values[field] : null;
  if (!value || normValue(field, value) !== normValue(field, currentValue)) return null;
  return (
    <div style={{ fontSize: 11, color: "#6f6c80", marginTop: 4 }}>
      {onFile.source === "office" ? "From this office's records" : "From Smilepass records"}
      {" — the claim form doesn't list it."}
    </div>
  );
}

/**
 * The fields a verification is reviewed on, as an edit surface.
 *
 * Renders REVIEW_FIELDS, which is CRITICAL_FIELDS plus `relation_to_member`.
 * That last one is shown but never required — it is not another identifier to
 * collect, it is the field that says who the other five DESCRIBE. See the note
 * on REVIEW_FIELDS in lib/policyCompleteness for why it is not a gate.
 *
 * Extracted from PolicyCompletionStep so the Insurance Requests review checklist
 * can present the SAME fields in a different frame without a second copy of the
 * list, the DOB suggester wiring or the missing-field markers. This codebase has
 * already been bitten by duplicating policy-completeness logic — one definition.
 *
 * Holds no state: `draft` is the host's in-progress edits, `onChange` reports
 * every keystroke back. Saving is the host's job, deliberately — every field
 * written through `updatePatientInsurancePolicy` is appended to the policy's
 * `manually_edited_fields` and locked against future PMS syncs, so only genuinely
 * changed values should ever be sent.
 *
 * @param {number} policyId
 * @param {Object} merged     policy + draft, i.e. what the user currently sees
 * @param {Object} draft      the host's in-progress edits for this policy
 * @param {Array}  missing    field names still missing, from missingCriticalFields
 * @param {Object} [preview]  the policy's Sikka diff ({sikka_values, field_states,
 *   local_snapshot}) from the auto-fetch response. Drives the inline "PMS says …
 *   · Use" suggestion under each field, and the "you previously set this to …"
 *   prompt on a locked field. Omit (modal flows) for no suggestions.
 * @param {number} patientId  for the DOB suggester when a policy has no id yet
 * @param {Object} [patient]  the patient record. Picking "Self" fills the member
 *   fields from it; without it the relation is still selectable and saved, it
 *   just does not self-fill. Only the request composer has one to pass.
 * @param {Function} onChange (policyId, field, value) => void
 * @param {Function} [getPopupContainer] where the DatePicker popup renders. antd
 *   portals to document.body by default, which is fine in a Modal but leaves the
 *   calendar unreachable inside a Drawer — pass `t => t.parentElement` there.
 *   Omit everywhere else to keep antd's default.
 */
export default function PolicyCriticalFields({ policyId, merged, draft, missing, preview, patientId, patient, onChange, getPopupContainer }) {
  const edits = draft || {};

  /**
   * Choosing "Self" states that the member IS the patient, so it fills the member
   * fields with the patient's own details rather than leaving someone to retype
   * them — the same rule, and the same reasoning, as the Manage Policies modal's
   * `handleRelationChange`.
   *
   * It only ever fires on an explicit pick, and switching AWAY from Self clears
   * those fields only while they still match the patient exactly. Anything edited
   * since is the user's and is left alone; what gets cleared is only ever this
   * autofill, which a spouse or child policy would otherwise carry as the wrong
   * member — the exact failure this field was added to prevent.
   */
  function handleRelationChange(next) {
    onChange(policyId, "relation_to_member", next || "");
    if (!patient) return;

    const dob = patient.birth_date || patient.date_of_birth;
    const parsed = dob ? dayjs(typeof dob === "string" ? dob.split("T")[0] : dob) : null;
    const mine = {
      first_name: patient.first_name || "",
      last_name: patient.last_name || "",
      date_of_birth: parsed && parsed.isValid() ? parsed.format("YYYY-MM-DD") : "",
    };

    if (next === "self") {
      Object.keys(mine).forEach(function (f) { onChange(policyId, f, mine[f]); });
      return;
    }

    const sameName = String(merged.first_name || "") === mine.first_name
      && String(merged.last_name || "") === mine.last_name;
    const currentDob = merged.date_of_birth
      ? dayjs(String(merged.date_of_birth).split("T")[0]).format("YYYY-MM-DD") : "";
    if (sameName && currentDob === mine.date_of_birth) {
      Object.keys(mine).forEach(function (f) { onChange(policyId, f, ""); });
    }
  }

  return (
    <>
      {REVIEW_FIELDS.map(function (field) {
        const isMissing = missing.indexOf(field) !== -1;
        const value = merged[field];
        return (
          <div key={field} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: "#6f6c80", marginBottom: 4 }}>
              {FIELD_LABELS[field]}
              {isMissing ? <span style={{ color: "#cf1322" }}> *</span> : null}
            </div>
            {field === "relation_to_member" ? (
              <>
                <Select
                  getPopupContainer={getPopupContainer}
                  style={{ width: "100%" }}
                  allowClear
                  placeholder="Who is the patient to the plan member?"
                  value={merged.relation_to_member || undefined}
                  options={RELATION_OPTIONS}
                  onChange={handleRelationChange}
                />
                {/* Only where it changes what gets sent. A dependent policy puts
                    the SUBSCRIBER in the member fields and the patient in the
                    carrier's separate claimant fields; leaving it on Self is what
                    sends the patient as the subscriber and gets the lookup
                    rejected. Silent for Self, which needs no explanation. */}
                {merged.relation_to_member && merged.relation_to_member !== "self" ? (
                  <div style={{ fontSize: 11, color: "#6f6c80", marginTop: 4 }}>
                    The fields above should be the plan member&rsquo;s, not the
                    patient&rsquo;s.
                  </div>
                ) : null}
              </>
            ) : field === "date_of_birth" ? (
              <>
                <DatePicker
                  getPopupContainer={getPopupContainer}
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  status={isMissing ? "warning" : ""}
                  value={value ? dayjs(value) : null}
                  onChange={function (d) {
                    onChange(policyId, field, d ? d.format("YYYY-MM-DD") : "");
                  }}
                />
                <MemberDobSuggestion
                  policyId={policyId}
                  patientId={patientId}
                  first={merged.first_name}
                  last={merged.last_name}
                  active={isMissing}
                  onUse={function (dob) {
                    onChange(policyId, field, dob);
                  }}
                />
              </>
            ) : (
              <Input
                value={edits[field] !== undefined ? edits[field] : value || ""}
                status={isMissing ? "warning" : ""}
                placeholder={FIELD_LABELS[field]}
                onChange={function (e) {
                  onChange(policyId, field, e.target.value);
                }}
              />
            )}
            <OnFileNote field={field} currentValue={value} preview={preview} />
            <PmsSuggestion
              field={field}
              currentValue={value}
              preview={preview}
              onUse={function (v) {
                // Route the relation through the same handler as the dropdown so a
                // "Self" pick still self-fills the member fields; everything else
                // just sets the value.
                if (field === "relation_to_member") handleRelationChange(v);
                else onChange(policyId, field, v);
              }}
            />
          </div>
        );
      })}
    </>
  );
}
